import { useEffect, useRef, useState } from 'react';
import styles from './GenerationProgress.module.css';

// Simulated progress for long-running generation requests. The backend gives us a
// single response after ~2 minutes, so real progress is unknowable — instead we
// advance along an asymptotic curve calibrated to the estimated duration: quick at
// first, decelerating as it approaches MAX_SIMULATED_PERCENT, and snapping to 100%
// only when the request actually finishes.
const MAX_SIMULATED_PERCENT = 96;
const TICK_INTERVAL_MS = 250;
const FINISH_DISPLAY_MS = 700;

// Stage messages as fractions of the estimated duration.
const STAGES = [
    { at: 0, label: 'Reading plan parameters and recent sessions…' },
    { at: 0.12, label: 'Analyzing recent completed sessions…' },
    { at: 0.33, label: 'Drafting exercises…' },
    { at: 0.62, label: 'Balancing intensity and recovery…' },
    { at: 0.87, label: 'Finalizing the training…' },
];

export const getSimulatedPercent = (elapsedSeconds, estimatedSeconds) => {
    // Time constant of estimate/3 puts the bar around 92% at the estimated
    // duration, leaving visible movement if the request runs long.
    const timeConstant = estimatedSeconds / 3;
    return MAX_SIMULATED_PERCENT * (1 - Math.exp(-elapsedSeconds / timeConstant));
};

export const getStageLabel = (elapsedSeconds, estimatedSeconds) => {
    let label = STAGES[0].label;
    STAGES.forEach((stage) => {
        if (elapsedSeconds >= stage.at * estimatedSeconds) {
            label = stage.label;
        }
    });
    return label;
};

const GenerationProgress = ({ active, succeeded = false, estimatedSeconds = 120 }) => {
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [isFinishing, setIsFinishing] = useState(false);
    const wasActiveRef = useRef(false);

    useEffect(() => {
        if (active) {
            wasActiveRef.current = true;
            setIsFinishing(false);
            setElapsedSeconds(0);
            const startedAt = Date.now();
            const interval = setInterval(() => {
                setElapsedSeconds((Date.now() - startedAt) / 1000);
            }, TICK_INTERVAL_MS);
            return () => clearInterval(interval);
        }

        if (wasActiveRef.current) {
            wasActiveRef.current = false;
            if (!succeeded) {
                // Failed or cancelled: hide immediately instead of flashing 100%.
                setIsFinishing(false);
                return undefined;
            }
            setIsFinishing(true);
            const timeout = setTimeout(() => setIsFinishing(false), FINISH_DISPLAY_MS);
            return () => clearTimeout(timeout);
        }

        return undefined;
    }, [active, succeeded]);

    if (!active && !isFinishing) {
        return null;
    }

    const percent = isFinishing
        ? 100
        : Math.min(getSimulatedPercent(elapsedSeconds, estimatedSeconds), MAX_SIMULATED_PERCENT);
    const roundedPercent = Math.round(percent);
    const label = isFinishing ? 'Done' : getStageLabel(elapsedSeconds, estimatedSeconds);
    const estimatedMinutes = Math.max(1, Math.round(estimatedSeconds / 60));

    return (
        <div className={styles.progressWrap} role="status" aria-live="polite">
            <div className={styles.progressHeader}>
                <span className={styles.progressLabel}>{label}</span>
                <span className={styles.progressPercent}>{roundedPercent}%</span>
            </div>
            <div
                className={styles.progressTrack}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={roundedPercent}
                aria-label="Training generation progress"
            >
                <div
                    className={`${styles.progressFill} ${isFinishing ? styles.progressFillDone : ''}`}
                    style={{ width: `${percent}%` }}
                />
            </div>
            {!isFinishing ? (
                <p className={styles.progressHint}>
                    {`This usually takes about ${estimatedMinutes} minute${estimatedMinutes > 1 ? 's' : ''} — you can keep working, we'll load the result into the form.`}
                </p>
            ) : null}
        </div>
    );
};

export default GenerationProgress;
